import React from 'react';
import { Modal, Radio } from 'antd';
import { HistoryOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import LOCALS from '../../../commons/locals';
import CashSettlementHistory from './cash-settlement-history';
import CashCountForm from './components/CashCountForm';
import CashPaymentSummaryCard from './components/CashPaymentSummaryCard';
import CashSettlementConfirmModal from './components/CashSettlementConfirmModal';
import PaymentRecordsCard from './components/PaymentRecordsCard';
import RecycleOrderDailyStatsCard from './components/RecycleOrderDailyStatsCard';
import { useCashSettlementPage } from './hooks/use-cash-settlement-page';

interface CashSettlementProps {
  selectedShopId?: number;
  showShopSelector?: boolean;
  showHistoryButton?: boolean;
}

const CashSettlement: React.FC<CashSettlementProps> = ({
  selectedShopId: externalSelectedShopId,
  showShopSelector = true,
  showHistoryButton = true,
}) => {
  const { t } = useTranslation();
  const page = useCashSettlementPage({ externalSelectedShopId });

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white shadow-sm border-b sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
              <span className="mr-2">
                {t(LOCALS.daily_register_close_title)}
              </span>
            </h1>

            {showHistoryButton && (
              <button
                onClick={() => page.setShowHistoryModal(true)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-medium transition-all duration-200 border border-gray-300"
                disabled={page.submitting}
              >
                <HistoryOutlined className="text-lg" />
                <span className="hidden sm:inline">
                  {t(LOCALS.daily_register_close_history_action)}
                </span>
              </button>
            )}
          </div>
        </div>
      </div>

      {showShopSelector && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-center">
            <Radio.Group
              options={page.shopOptionList}
              optionType="button"
              buttonStyle="solid"
              value={page.selectedShopId}
              onChange={(e) => {
                page.handleShopChange(e.target.value);
              }}
            />
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-8">
        <div className="grid grid-cols-1 gap-8">
          <div className="bg-white rounded-lg shadow-sm border p-6">
            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 xl:gap-0 xl:divide-x xl:divide-gray-200">
              <PaymentRecordsCard
                titleLabel={LOCALS.daily_register_close_cashless_sales}
                payments={page.settlementData.creditCardPayments}
                paymentAccountList={page.settlementData.paymentAccountList}
                currencySymbol={page.currencySymbol}
                formatAmount={page.formatAmount}
                className="min-w-0 xl:pr-6"
              />
              <PaymentRecordsCard
                titleLabel={LOCALS.daily_register_close_cash_sales}
                payments={page.settlementData.cashOrderPayments}
                paymentAccountList={page.settlementData.paymentAccountList}
                currencySymbol={page.currencySymbol}
                formatAmount={page.formatAmount}
                totalLabel={LOCALS.daily_register_close_cash_sales_total}
                emptyLabel={LOCALS.daily_register_close_no_cash_sales_today}
                totalContainerClassName="bg-green-50 border-green-200"
                totalAmountClassName="text-green-600"
                className="min-w-0 pt-6 border-t border-gray-200 xl:pt-0 xl:pl-6 xl:border-t-0"
              />
            </div>
          </div>

          <CashPaymentSummaryCard
            records={page.settlementData.cashPaymentSummaryList}
            currencySymbol={page.currencySymbol}
            formatAmount={page.formatAmount}
          />

          <RecycleOrderDailyStatsCard
            records={page.settlementData.recycleOrderDailyStats}
          />

          {page.settlementData.refundOrderPayments.length > 0 && (
            <div className="bg-white rounded-lg shadow-sm border p-6">
              <PaymentRecordsCard
                titleLabel={LOCALS.daily_register_close_refund_payments}
                payments={page.settlementData.refundOrderPayments}
                paymentAccountList={page.settlementData.paymentAccountList}
                currencySymbol={page.currencySymbol}
                formatAmount={page.formatAmount}
                totalLabel={LOCALS.daily_register_close_refund_payments_total}
                emptyLabel={LOCALS.daily_register_close_no_refund_payments_today}
                totalContainerClassName="bg-red-50 border-red-200"
                totalAmountClassName="text-red-600"
              />
            </div>
          )}

          <CashCountForm
            values={page.cashData}
            currencySymbol={page.currencySymbol}
            amountPrecision={page.amountPrecision}
            submitting={page.submitting}
            showCashBreakdown={page.showCashBreakdown}
            bills={page.currentDenominations.bills}
            coins={page.currentDenominations.coins}
            saleCashTotal={page.settlementAmounts.saleCashTotal}
            recycledConsignmentCashTotal={
              page.settlementAmounts.recycledConsignmentCashTotal
            }
            dailyCashChangeAmount={page.settlementData.dailyCashChangeAmount}
            cashRefundTotal={page.settlementAmounts.cashRefundTotal}
            systemCashSales={page.settlementAmounts.systemCashSales}
            treasuryCashMovementNet={
              page.settlementAmounts.treasuryCashMovementNet
            }
            expectedCash={page.settlementAmounts.expectedCash}
            variance={page.settlementAmounts.variance}
            cashBreakdownTotal={page.cashBreakdownTotal}
            submitDisabled={page.submitDisabled}
            formatAmount={page.formatAmount}
            formatInputAmount={page.formatInputAmount}
            parseInputAmount={page.parseInputAmount}
            onTreasuryCashChange={page.handleTreasuryCashChange}
            onActualCashChange={(value) =>
              page.setCashData((prev) => ({
                ...prev,
                actualCash: value === null ? 0 : value,
              }))
            }
            onNoteChange={(note) =>
              page.setCashData((prev) => ({ ...prev, note }))
            }
            onCashBreakdownChange={page.handleCashBreakdownChange}
            onClearCashBreakdown={() =>
              page.setCashData((prev) => ({
                ...prev,
                cashBreakdown: {
                  bills: {},
                  coins: {},
                },
              }))
            }
            onToggleCashBreakdown={() =>
              page.setShowCashBreakdown((isVisible) => !isVisible)
            }
            onSubmit={page.handleSubmit}
          />
        </div>
      </div>

      {page.showConfirmModal && (
        <CashSettlementConfirmModal
          summary={page.confirmSummary}
          currencySymbol={page.currencySymbol}
          submitting={page.submitting}
          formatAmount={page.formatAmount}
          onCancel={() => page.setShowConfirmModal(false)}
          onConfirm={page.handleConfirmSubmit}
        />
      )}

      <Modal
        title={t(LOCALS.daily_register_close_history_title)}
        open={page.showHistoryModal}
        onCancel={() => page.setShowHistoryModal(false)}
        footer={null}
        width="90%"
        style={{ maxWidth: '1200px', top: 20 }}
        destroyOnClose
      >
        <CashSettlementHistory
          shopId={page.selectedShopId}
          onClose={() => page.setShowHistoryModal(false)}
        />
      </Modal>
    </div>
  );
};

export default CashSettlement;
